// @flow

import React, { useCallback, useEffect, useRef, useState } from "react"
import { LANGUAGES } from "shared/locales"
import LanguageIcon from "shared/resources/LanguageIcon"
import IntlStore from "shared/stores/IntlStore"
import styled from "styled-components"

const Button = styled.div.withConfig({
  shouldForwardProp: prop => !["isOpen"].includes(prop)
})`
  display: table;
  opacity: ${p => p.isOpen && "50%"};
  border: 1px solid ${props => (props.theme === "dark" ? "white" : "black")};
  padding: 5px 12px;
  border-radius: 200px;
`
const Language = styled.span`
  vertical-align: middle;
  color: ${props => (props.theme === "dark" ? "white" : "black")};
`
const DropdownContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  justify-content: center;
  align-content: center;
  margin: 0 auto;
  cursor: pointer;
  font-size: 13px;
`
const DropdownListContainer = styled.div`
  position: absolute;
  z-index: 9999;
  top: 45px;
  width: 100%;
  border: none;
  border-radius: 8px;
  overflow: hidden;
  min-width: 100px;
  font-size: 13px;
`
const DropdownList = styled.ul`
  padding: 0;
  margin: 0;
  background: #fff;
  box-sizing: border-box;
  color: #000;
  font-weight: 500;
`
const ListItem = styled.li.withConfig({
  shouldForwardProp: prop => !["isSelected"].includes(prop)
})`
  list-style: none;
  border-bottom: 1px solid #eaeaea;
  padding: 15px 10px;
  color: #656565;
  font-weight: bold;
  &:last-child {
    border: none;
  }
  ${p => p.isSelected && "color: #000"}
`
const Icon = styled.span`
  display: table-cell;
  vertical-align: middle;
  padding-right: 6px;
  svg {
    vertical-align: middle;
    fill: ${props => (props.theme === "dark" ? "white" : "black")};
  }
`
const languageNames = {
  SV: "Svenska",
  EN: "English",
  NO: "Norsk",
  FI: "Suomi"
}

type Props = {
  theme: string
}

const LanguageSelector = ({ theme }: Props): React$Element<any> => {
  const ref = useRef(null)
  const currentLocale = IntlStore.locale
  const selectedLanguage = currentLocale.name
  const [open, setOpen] = useState(false)

  const clickOrEscapeListener = useCallback(
    (e: MouseEvent | KeyboardEvent) => {
      const isEscapeEvent = e instanceof KeyboardEvent && e.key === "Escape"
      const isOutsideClick =
        e instanceof MouseEvent &&
        ref.current &&
        !ref.current.contains(e.target)

      if (isEscapeEvent || isOutsideClick) {
        onClose()
      }
    },
    [ref.current]
  )

  useEffect(() => {
    if (open) {
      document.addEventListener("click", clickOrEscapeListener)
      document.addEventListener("keyup", clickOrEscapeListener)
    } else {
      document.removeEventListener("click", clickOrEscapeListener)
      document.removeEventListener("keyup", clickOrEscapeListener)
    }
    return () => {
      document.removeEventListener("click", clickOrEscapeListener)
      document.removeEventListener("keyup", clickOrEscapeListener)
    }
  }, [open])

  const onClose = () => {
    setOpen(false)
  }

  const handleLanguageChange = selectedLanguage => {
    setOpen(false)
    IntlStore.setSelectedLanguage(selectedLanguage)
  }

  return (
    <DropdownContainer ref={ref}>
      <Button onClick={() => setOpen(!open)} isOpen={open} theme={theme}>
        <Icon theme={theme}>{LanguageIcon}</Icon>
        <Language theme={theme}>
          {languageNames[currentLocale.name.toUpperCase()]}
        </Language>
      </Button>
      {open && (
        <DropdownListContainer>
          <DropdownList>
            {Object.keys(LANGUAGES).map(lang => (
              <ListItem
                key={lang}
                onClick={() => handleLanguageChange(lang.toLocaleLowerCase())}
                isSelected={selectedLanguage === lang.toLocaleLowerCase()}
              >
                {languageNames[lang]}
              </ListItem>
            ))}
          </DropdownList>
        </DropdownListContainer>
      )}
    </DropdownContainer>
  )
}

export default LanguageSelector
